import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type PanInfo } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CHECKOUT_URL, galleryImages } from '../data/salesContent';
import { trackInitiateCheckout } from '../utils/metaPixel';
const AUTOPLAY_MS = 4500;
export function GalleryCarousel() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [paused, setPaused] = useState(false);
  const dragged = useRef(false);
  const reduceMotion = useReducedMotion();
  const count = galleryImages.length;
  const go = useCallback((next: number, dir: number) => {
    setState([(next + count) % count, dir]);
  }, [count]);
  useEffect(() => {
    if (paused || reduceMotion) return;
    const id = setTimeout(() => go(index + 1, 1), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [index, paused, reduceMotion, go]);
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (Math.abs(info.offset.x) > 60) {
      dragged.current = true;
      go(index + (info.offset.x < 0 ? 1 : -1), info.offset.x < 0 ? 1 : -1);
    }
  };
  const current = galleryImages[index];
  const arrowClass = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-700 bg-zinc-950/90 text-zinc-50 transition-colors duration-150 hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50';
  return <div className="mx-auto max-w-lg" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} role="region" aria-roledescription="carrusel" aria-label="Imágenes del Mega Pack" data-id="element-27" data-mp="d0fhp425x14c">
      <div className="relative" data-id="element-28" data-mp="38022dec8omm">
        <div className="relative mx-auto aspect-[643/805] w-full overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl shadow-black/50" data-id="element-29" data-mp="qzovxy6t5l5o">
          <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-30" data-mp="vsi28qhxd1jv">
            <motion.a key={index} href={CHECKOUT_URL} onClick={e => {
            if (dragged.current) {
              e.preventDefault();
              dragged.current = false;
              return;
            }
            trackInitiateCheckout();
          }} custom={direction} initial={{
            opacity: 0,
            x: direction * 40
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction * -40
          }} transition={{
            duration: 0.28,
            ease: [0.23, 1, 0.32, 1]
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.2} onDragEnd={onDragEnd} className="absolute inset-0 block cursor-grab focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-accent/50 active:cursor-grabbing" aria-label={`${current.alt} — comprar el Mega Pack`} data-id="element-31" data-mp="15lunnc54iqbr">
              <img src={current.src} alt={current.alt} draggable={false} className="h-full w-full select-none object-contain" data-id="element-32" data-mp="2alswisf3xf71" />
            </motion.a>
          </AnimatePresence>
          <p className="pointer-events-none absolute bottom-3 right-3 rounded-md bg-zinc-950/85 px-2.5 py-1 text-xs font-semibold tabular-nums text-zinc-50" data-id="element-33" data-mp="2fmqa0rp69l27">
            {index + 1} / {count}
          </p>
        </div>
        <button type="button" onClick={() => go(index - 1, -1)} aria-label="Imagen anterior" className={`${arrowClass} left-2 md:-left-5`} data-id="element-34" data-mp="1rv1buupo62dc">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-35" data-mp="1wuxjr51hmdy6" />
        </button>
        <button type="button" onClick={() => go(index + 1, 1)} aria-label="Imagen siguiente" className={`${arrowClass} right-2 md:-right-5`} data-id="element-36" data-mp="20vhe60uof3gm">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-37" data-mp="25nr59k7j29kh" />
        </button>
      </div>

      <ul className="mt-5 grid grid-cols-6 gap-2 md:gap-3" data-id="element-38" data-mp="194bd9i74t1qa">
        {galleryImages.map((img, i) => <li key={img.src} data-id="element-39" data-mp="1d4c11tg4epa0" data-mpi={i}>
            <button type="button" onClick={() => go(i, i > index ? 1 : -1)} aria-label={`Ver imagen ${i + 1}`} aria-current={i === index} className={`block aspect-[4/5] w-full overflow-hidden rounded-md border-2 bg-white transition-[border-color,opacity] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ${i === index ? 'border-accent opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`} data-id="element-40" data-mp="u1jmearmf42o">
              <img src={img.src} alt="" className="h-full w-full object-cover object-top" data-id="element-41" data-mp="1cr8bvocpdsan" />
            </button>
          </li>)}
      </ul>
    </div>;
}